// File: apps/app-web/src/hooks/useRecordPayment.ts
// Purpose: Hook for recording cash/check/pre-payments for subscription enrollments

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

export interface RecordPaymentParams {
  enrollmentId: string;
  amount: number;
  paymentMethod: 'cash' | 'check' | 'bank_transfer' | 'other';
  billingDate?: string; // ISO date string, defaults to today
  notes?: string;
  isPrepayment?: boolean;
}

export interface RecordPaymentResponse {
  success: boolean;
  payment_id?: string;
  enrollment_id?: string;
  prepayment_months_paid?: number;
  next_billing_date?: string;
  message?: string;
  error?: string;
}

export function useRecordPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: RecordPaymentParams): Promise<RecordPaymentResponse> => {
      const { enrollmentId, amount, paymentMethod, billingDate, notes, isPrepayment } = params;

      // Call record_payment RPC function
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)('record_payment', {
        p_enrollment_id: enrollmentId,
        p_amount: amount,
        p_payment_method: paymentMethod,
        p_billing_date: billingDate || new Date().toISOString().split('T')[0],
        p_notes: notes || null,
        p_is_prepayment: isPrepayment || false,
      });

      if (error) {
        throw new Error(error.message || 'Failed to record payment');
      }

      return data as RecordPaymentResponse;
    },
    onSuccess: () => {
      // Invalidate all subscription-related queries
      queryClient.invalidateQueries({ queryKey: ['subscriptionsWithAmountDue'] });
      queryClient.invalidateQueries({ queryKey: ['local-subscription-enrollments'] });
      queryClient.invalidateQueries({ queryKey: ['user-subscriptions-with-discounts'] });
      queryClient.invalidateQueries({ queryKey: ['child-subscriptions-with-discounts'] });
      queryClient.invalidateQueries({ queryKey: ['children-subscriptions-with-discounts'] });
      queryClient.invalidateQueries({ queryKey: ['billing-account-subscriptions-with-discounts'] });
      queryClient.invalidateQueries({ queryKey: ['payment-records'] });
      queryClient.invalidateQueries({ queryKey: ['recentManualPayments'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
    },
  });
}
